import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  MultiSelect,
  Select,
  Text,
  Tooltip, Accordion
} from "@mantine/core";
import {
  IStartDataCollectionStep
} from "../../../types/protocol";
import {
  IStartDataCollectionStepDefinition,
  IDirConfig,
} from "../../../types/protocolDefinitions";
import { TSubjectId } from "@/common/types";
import { TAnalysisGroup } from "../../../types/analysis";
import stepClasses from "../StepView.module.css";
import commonClasses from "@/common/common.module.css";
import { IconAlertCircle, IconPlus } from "@tabler/icons-react";
import {
  buildUploaderConfigs,
  useTauriAvailability,
} from "./DataCollection";
import { showErrorNotification, showSuccessNotification } from "@/common/notifications";
import * as tauriService from "@/common/services/tauriService";
import { useUploadStatusStore } from "@/protocols/persistence/useUploadStatusStore";
import { DeepPartial } from "@/utils/types/DeepPartial";
import cx from "clsx";
import { DEFAULT_DIR_CONFIG, generateId, getPresetDefaults } from "./constants";
import { DirConfigRow } from "./components/DirConfigRow";
import { useNeurotuneStore } from "@/common/store/neurotune.ts";
import { isAria } from "@/common/utils/companies.ts";
import { isSubdirectory } from "@/common/utils/fileUtils"
import { TWorkflow } from "../StepView";

const getExtensions = (config: IDirConfig): string[] => {
  if (config.uploader_preset && config.uploader_preset !== "custom") {
    const defaults = getPresetDefaults(config.uploader_preset);
    if (defaults.extensions.length > 0) {
      return defaults.extensions;
    }
  }
  if (config.uploader_custom_extensions && config.uploader_custom_extensions.length > 0) {
    return config.uploader_custom_extensions.map(ext => ext.extension);
  }
  return [];
};

const validateConfigOverlap = (config: IDirConfig, otherConfigs: IDirConfig[]): string | null => {
  if (!config.dir_path || !config.recursive) return null;

  const configExts = getExtensions(config);
  if (configExts.length === 0) return null;

  for (const other of otherConfigs) {
    if (!other.dir_path || other.id === config.id) continue;

    const otherExts = getExtensions(other);
    if (otherExts.length === 0) continue;

    const commonExts = configExts.filter(ext => otherExts.includes(ext));
    if (commonExts.length === 0) continue;

    if (
      config.dir_path.localeCompare(other.dir_path) === 0 ||
      isSubdirectory(config.dir_path, other.dir_path) && config.recursive ||
      isSubdirectory(other.dir_path, config.dir_path) && other.recursive) {
      return "Directory conflict, you cannot have overlapping directories with the same extensions.";
    }
  }

  return null;
};

type StartDataCollectionStepType = IStartDataCollectionStep | IStartDataCollectionStepDefinition;

export interface StartDataCollectionStepViewProps {
  step: StartDataCollectionStepType;
  subjectId?: TSubjectId;
  onChange: (updates: DeepPartial<StartDataCollectionStepType>) => void;
  workflow?: TWorkflow;
}

export const StartDataCollectionStepView: React.FC<StartDataCollectionStepViewProps> = ({
  step,
  subjectId: _subjectId,
  onChange,
  workflow = "session",
}) => {
  const activeCompany = useNeurotuneStore((state) => state.activeCompany);
  const isPlanningMode = workflow === "planning";
  const isReadOnly = workflow === "review";
  const adapterPlacement = step.aria?.adapter_placement;
  const analysisGroups = step.aria?.analysisGroups || [];

  const isTauri = useTauriAvailability(!isPlanningMode);
  const isCollecting = useUploadStatusStore(
    (s) => !isPlanningMode && (s.dataCollectionStatus?.watcherRunning ?? false),
  );
  const [isStopping, setIsStopping] = useState(false);

  const toggleDataCollection = useCallback(async () => {
    if (!isTauri || isPlanningMode) return;
    const typedStep = step as IStartDataCollectionStep;
    if (isCollecting) {
      if (isStopping) return;
      setIsStopping(true);
      try {
        await tauriService.stopDataCollection();
        await useUploadStatusStore.getState().refresh(activeCompany.id, typedStep.protocolId);
      } catch (error) {
        showErrorNotification({
          title: "Data Collection Stop Failed",
          message: error instanceof Error ? error.message : String(error),
        });
      } finally {
        setIsStopping(false);
      }
    } else {
      try {
        const uploaderConfigs = buildUploaderConfigs(typedStep.dir_configs, typedStep.data_dir_path);
        if (uploaderConfigs.length === 0) {
          return showErrorNotification({
            title: "Invalid Data Collection Configuration",
            message: "You must specify at least 1 directory and 1 file extension for data collection",
          });
        }
        const sources = uploaderConfigs.map((c) => ({
          dirPath: c.dir_path ?? typedStep.data_dir_path ?? "",
          recursive: c.recursive ?? false,
          extensions: c.extensions,
          minFileAgeSeconds: c.min_file_age,
          backupSourceOnCopy: c.after_upload === "backup",
        }));
        await tauriService.startDataCollection({
          companyId: activeCompany.id,
          protocolId: typedStep.protocolId,
          sources,
        });
        showSuccessNotification({
          title: "Data Collection Started",
          message: "Data collection has started.",
        });
        await useUploadStatusStore.getState().refresh(activeCompany.id, typedStep.protocolId);
      } catch (error) {
        showErrorNotification({
          title: "Data Collection has failed to start.",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    }
  }, [isTauri, isPlanningMode, isCollecting, isStopping, step, activeCompany]);

  const dirConfigs = step.dir_configs || [];

  const overlapErrors = useMemo(() => {
    const errors = new Map<string, string | null>();
    dirConfigs.forEach(config => {
      errors.set(config.id, validateConfigOverlap(config, dirConfigs));
    });
    return errors;
  }, [dirConfigs]);

  const hasValidConfig = (): boolean => {
    if (dirConfigs.length === 0) {
      return false;
    }
    return dirConfigs.every(config => config.dir_path) &&
      !Array.from(overlapErrors.values()).some(Boolean);
  };

  const handleAddDirectory = () => {
    const newConfigs = [...dirConfigs, { ...DEFAULT_DIR_CONFIG, id: generateId() }];
    onChange({ dir_configs: newConfigs });
  };

  const handleUpdateConfig = (index: number, updates: Partial<IDirConfig>) => {
    const newConfigs = [...dirConfigs];
    newConfigs[index] = { ...newConfigs[index], ...updates };
    onChange({ dir_configs: newConfigs });
  };

  const handleRemoveConfig = (index: number) => {
    const newConfigs = dirConfigs.filter((_, i) => i !== index);
    onChange({ dir_configs: newConfigs });
  };
  const renderDirectories = () => {
    return dirConfigs.map((config, index) => (
      <Box key={`dir-${index}`} pb={"0.50rem"} style={{
        display: "grid",
        grid: "auto / 1fr",
        alignItems: "end",
        borderTop: "var(--bios-border)",
        width: "100%"
      }}>
        <DirConfigRow
          key={config.id}
          config={config}
          onUpdate={(updates) => handleUpdateConfig(index, updates)}
          onRemove={() => handleRemoveConfig(index)}
          showRemove={dirConfigs.length > 1}
          disabled={isCollecting || isReadOnly}
          error={overlapErrors.get(config.id) || undefined}
        />
      </Box>
    ))

  }

  return (
    <div className={stepClasses.daqStepViewField}>
      <Box style={{
        gridColumn: "1 / -1",
        display: "grid",
        grid: "auto / auto 1fr auto",
        columnGap: "0.5em",
        alignItems: "end"
      }}>
        {isAria(activeCompany) ? (
          <>
            <Box className={stepClasses.stepViewField}>
              <Select
                label={"Adapter"}
                size="sm"
                miw={"6rem"}
                placeholder="Select"
                value={adapterPlacement}
                data={["Left", "Right"]}
                className={stepClasses.stepViewSelectNarrow}
                onChange={(value) => onChange({
                  aria: {
                    adapter_placement: value as "Left" | "Right",
                    analysisGroups: step.aria?.analysisGroups || []
                  }
                })}
                disabled={isReadOnly}
              />
            </Box>
            <Box className={stepClasses.stepViewField}>
              <MultiSelect
                size="sm"
                label={"Analysis"}
                placeholder="Select"
                value={analysisGroups}
                data={Object.values(TAnalysisGroup)}
                className={stepClasses.stepViewMultiSelect}
                styles={{
                  pillsList: {
                    gap: "0.125rem",
                  },
                  input: {
                    minHeight: "unset",
                  },
                  inputField: {
                    minWidth: "3em",
                  },
                }}
                onChange={(value) => onChange({
                  aria: {
                    adapter_placement: step.aria?.adapter_placement,
                    analysisGroups: value as Array<TAnalysisGroup>
                  }
                })}
                disabled={isReadOnly}
              />
            </Box>
          </>
        ) : (<><Box /><Box /></>)
        }
      </Box>
      <Box style={{ marginTop: "0.5rem", gridColumn: "1 / -1" }}>
        {workflow === "review" || workflow == "review_edit" ?
          renderDirectories() :
          (
            <Accordion defaultValue="files" variant="contained">
              <Accordion.Item value="files">
                <Accordion.Control bg={"var(--bios-medium-blue-1)"}>
                  <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Text>File Collection</Text>
                  </Box>
                </Accordion.Control>
                <Accordion.Panel bg={"var(--bios-medium-blue-1)"}>
                  {workflow === "session" ? (
                    <Box style={{
                      display: "grid",
                      grid: "auto  / auto 1fr",
                      alignItems: "start",
                      justifySelf: "center",
                      columnGap: "0.5rem"
                    }}>
                      {!isTauri ? (
                        <Tooltip
                          label={<Box>These automatic controls are only available when
                            executed in
                            BIOS
                            Neurotune
                            Data
                            Collector.</Box>}>
                          <IconAlertCircle size="1em" color="var(--bios-color-warning)"
                          />
                        </Tooltip>
                      ) : <Box />}
                      {!isTauri || !hasValidConfig() || isStopping ? (
                        <Tooltip
                          label={
                            !isTauri
                              ? "Automatic controls require BIOS Neurotune Data Collector"
                              : !hasValidConfig()
                                ? "Configure directory paths to enable"
                                : isStopping
                                  ? "Stopping in progress..."
                                  : undefined
                          }
                        >
                          <Button
                            size="compact-xs"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              void toggleDataCollection();
                            }}
                            disabled={!isTauri || !hasValidConfig() || isStopping}
                          >
                            {isStopping ? "Stopping..." : (isCollecting ? "Stop" : "Start")}
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          size="compact-xs"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            void toggleDataCollection();
                          }}
                          disabled={!isTauri || !hasValidConfig() || isStopping}
                        >
                          {isStopping ? "Stopping..." : (isCollecting ? "Stop" : "Start")}
                        </Button>
                      )}

                    </Box>
                  ) : <div />}
                  {dirConfigs.length === 0 && (
                    <Box mb="sm">
                      <Text size="xs" c="dimmed">
                        No directories configured. Add a directory to configure file
                        acquisition.
                      </Text>
                    </Box>
                  )}
                  <Button
                    variant="transparent"
                    className={cx(commonClasses.hover)}
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={handleAddDirectory}
                    disabled={isCollecting || workflow === "review"}
                    style={{ marginTop: "0.5rem" }}
                  >
                    Add Acquisition Directory
                  </Button>
                  {renderDirectories()}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )
        }
      </Box>
    </div>
  );
};
